import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import DataTableComponent from "components/DataTableComponent";
import AlertNotification from 'components/globals/AlertNotification';
import RecordDetailOffcanvas from "components/RecordDetailOffcanvas";

const InnovativeIdeaList = () => {
    const navigate = useNavigate();
    const [innovativeIdeas, setIdeas] = useState([]);
    const location = useLocation();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Default to current year
    const [availableYears, setAvailableYears] = useState([]);
    const [previewInnovativeIdea, setPreviewInnovativeIdea] = useState(null);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    useEffect(() => {
        const fetchInnovativeIdeas = async (year = selectedYear) => {
            try {
                const response = await apiClient.get(`/innovative-ideas/?year=${year}`);
                setIdeas(response.data.innovative_ideas);
                setPreviewInnovativeIdea((current) => current
                    ? response.data.innovative_ideas.find((idea) => idea.id === current.id) || null
                    : null);
                setAvailableYears(response.data.available_years);
            } catch (error) {
                console.error('Error fetching ideas:', error);
            }
        };

        fetchInnovativeIdeas(selectedYear);
    }, [location.pathname, selectedYear]);

    const handleDelete = async (innovativeIdeaId) => {
        try {
            await apiClient.delete(`/innovative-ideas/${innovativeIdeaId}/`);
            setIdeas((current) => current.filter((innovativeIdea) => innovativeIdea.id !== innovativeIdeaId));
            setPreviewInnovativeIdea((current) => current?.id === innovativeIdeaId ? null : current);
        } catch (error) {
            console.error('Error deleting idea:', error);
        }
    };

    const columns = [
        { name: "Código", selector: (row) => row.code, sortable: true, className: 'text-center' },
        { name: "Designação", selector: (row) => row.designation, sortable: true, grow: 2 },
        { name: "Origem", selector: (row) => row.origin, sortable: true },
        { name: "Data", selector: (row) => row.date, sortable: true },
        {
            name: "Natureza", selector: (row) => {
                let types = [];
                
                if (row.organizational) types.push("Organizacional");
                if (row.service) types.push("Serviço");
                if (row.marketing) types.push("Marketing");
                if (row.process) types.push("Processo");
                if (row.product) types.push("Produto");
                
                return types.length > 0 ? types.join(", ") : "";
            }, sortable: true
        },
        { name: "Avaliação", selector: (row) => row.evaluation_score, sortable: true },
        { name: "Resultado da avaliação", selector: (row) => row.evaluation, sortable: true },
        { name: "Decisão", selector: (row) => row.state, sortable: true, cell: (row) => (
            <div
              style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', cursor: 'pointer' }}
              onClick={() => setPreviewInnovativeIdea(row)}
            >
              {row.state}
            </div>
          )},
    ];

    return (
        <>
            <AlertNotification />
            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">Ideias Inovadoras</h3>
                    <Link to="/ideias-inovadoras/new">
                        <CButton color="info" className="text-white">Nova Ideia Inovadora</CButton>
                    </Link>
                </CCardHeader>
                <CCardBody>
                    <DataTableComponent
                        columns={columns}
                        data={innovativeIdeas}
                        years={availableYears}  // Optional
                        selectedYear={selectedYear}  // Optional
                        onYearChange={(newYear) => setSelectedYear(newYear)}  // Optional
                        onRowClicked={setPreviewInnovativeIdea}
                    />
                </CCardBody>
            </CCard>
            <RecordDetailOffcanvas
                record={previewInnovativeIdea}
                onHide={() => setPreviewInnovativeIdea(null)}
                title="Ideia Inovadora"
                subtitle={previewInnovativeIdea?.code}
                fields={previewInnovativeIdea ? [
                    { label: "Código", value: previewInnovativeIdea.code },
                    { label: "Designação", value: previewInnovativeIdea.designation },
                    { label: "Origem", value: previewInnovativeIdea.origin },
                    { label: "Data", value: previewInnovativeIdea.date },
                    {
                        label: "Natureza",
                        value: [
                            previewInnovativeIdea.organizational && "Organizacional",
                            previewInnovativeIdea.service && "Serviço",
                            previewInnovativeIdea.marketing && "Marketing",
                            previewInnovativeIdea.process && "Processo",
                            previewInnovativeIdea.product && "Produto",
                        ].filter(Boolean).join(", "),
                    },
                    { label: "Avaliação", value: previewInnovativeIdea.evaluation_score },
                    { label: "Resultado da avaliação", value: previewInnovativeIdea.evaluation },
                    { label: "Decisão", value: previewInnovativeIdea.state },
                ] : []}
                headerActions={previewInnovativeIdea && <>
                    <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/ideias-inovadoras/${previewInnovativeIdea.id}`)}>
                        {!["Executar no Imediato", "Descartar"].includes(previewInnovativeIdea.state) &&
                        (user_id === previewInnovativeIdea.responsible || is_director) ? "Editar" : "Ver"}
                    </CButton>
                    {is_director && (
                        <CButton color="danger" size="sm" className="text-white" onClick={() => {
                            if (window.confirm("Tem a certeza que quer eliminar este registo?")) handleDelete(previewInnovativeIdea.id);
                        }}>
                            Eliminar
                        </CButton>
                    )}
                </>}
            />
        </>
    );
};

export default InnovativeIdeaList;
